import { PlanCycleHistoricalUsageData } from "@functions/services/billingUsage/planCycleHistoricalUsage";
import {
    FetchState,
    Workspace,
    WorkspaceUsage,
    WorkspaceUBPSubscriptionData,
    PaidPlanSubscriptionData,
    Dataset
} from "../types";
import { deepSet } from "../utils";
import experiments from "../../scripts/experiments";

interface Experiment {
    enabled: boolean;
    value: any;
    open: boolean;
    readable: string;
    shortReadable: string;
}

// Type definitions for training capabilities
interface TrainingUserCapabilities {
    isPaidPlan: boolean;
    canTrainAccurateModel: boolean;
    canTrainAllModels: boolean;
    canTrainExtraLarge: boolean;
    canTrainNas: boolean;
    canTrainNewRFDETR: boolean;
    canTrainSam3: boolean;
}

export interface WorkspaceState {
    currentWorkspaceId: Workspace["id"];
    isSetup: boolean;
    getUsage: () => WorkspaceUsage;
    getWorkspace: () => Workspace;
    getWorkspaceStarredDatasets: (id?: Workspace["id"]) => Dataset["id"][];
    getWorkspacesByUrl: () => Record<Workspace["url"], Workspace>;

    setCurrentWorkspaceId: (id: string) => any;
    setIsSetup: (isSetup: boolean) => any;
    addWorkspaceById: (ws: Workspace) => any;
    addWorkspaceUsageById: (usg: WorkspaceUsage) => any;
    addWorkspaceDatasetsById: (workspaceId: string, datasets: Dataset[]) => any;
    addWorkspaceStarredDatasetsById: (workspaceId: string, datasetIds: Dataset["id"][]) => any;
    workspacesById: Record<Workspace["id"], Workspace>;
    workspaceUsagesById: Record<Workspace["id"], WorkspaceUsage>;
    workspaceDatasetsById: Record<Workspace["id"], Record<Dataset["id"], Dataset>>;
    workspaceStarredDatasetsById: Record<Workspace["id"], Dataset["id"][]>;

    workspacePlanHistoricalUsageDataById: Record<Workspace["id"], PlanCycleHistoricalUsageData>;
    addWorkspacePlanHistoricalUsageDataById: (
        id: string,
        data: PlanCycleHistoricalUsageData
    ) => any;

    currentWorkspaceUBPCreditCapRestricted: boolean;
    setCurrentWorkspaceUBPCreditCapRestricted: (restricted: boolean) => any;

    workspaceUBPSubscriptionsById: Record<
        Workspace["id"],
        FetchState<WorkspaceUBPSubscriptionData>
    >;
    fetchWorkspaceUBPStripeSubscriptions: (id: string, refetch?: boolean) => Promise<void>;

    paidPlanSubscriptionById: Record<Workspace["id"], FetchState<PaidPlanSubscriptionData>>;
    fetchPaidPlanSubscription: (id: string, { refetch }?: { refetch?: boolean }) => Promise<void>;

    ssoWorkspaceNoSeats: boolean;
    ssoWorkspaceNoSeatsId: string | null;
    setSsoWorkspaceNoSeats: (noSeats: boolean, workspaceId: string | null) => any;

    getCurrentUserEmail: () => string | undefined;
    getExperimentState: (key: string) => Experiment;
    getTrainingUserCapabilities: () => TrainingUserCapabilities | null;
}

function workspaceReducer(set, get): WorkspaceState {
    return {
        // values
        currentWorkspaceId: null,
        workspacesById: {},
        isSetup: false,
        workspaceUsagesById: {},
        workspaceStarredDatasetsById: {},
        workspacePlanHistoricalUsageDataById: {},
        workspaceDatasetsById: {},
        currentWorkspaceUBPCreditCapRestricted: false,
        paidPlanSubscriptionById: {},
        ssoWorkspaceNoSeats: false,
        ssoWorkspaceNoSeatsId: null,

        // getters
        getWorkspace() {
            // fall back to universeSlice.userAsWorkspace if currentWorkspaceId is not set
            return get().workspacesById[get().currentWorkspaceId] || get().userAsWorkspace;
        },

        getWorkspaceStarredDatasets(id = get().currentWorkspaceId) {
            return get().workspaceStarredDatasetsById[id] ?? [];
        },

        getUsage() {
            // fall back to universeSlice.userAsUsage if currentWorkspaceId is not set
            return get().workspaceUsagesById[get().currentWorkspaceId] || get().userAsUsage;
        },

        getWorkspacesByUrl() {
            return Object.values(get().workspacesById).reduce((acc, ws) => {
                acc[ws.url] = ws;
                return acc;
            }, {}) as Record<Workspace["url"], Workspace>;
        },

        getCurrentUserEmail() {
            const workspace = get().workspacesById[get().currentWorkspaceId];
            const userId = get().user?.uid;
            return workspace?.members?.[userId]?.email;
        },

        getExperimentState(key: string) {
            const usage = get().getUsage();
            const experiment = experiments[key];

            if (!experiment) return { enabled: false, value: null, open: false, readable: "" };

            return {
                enabled: experiment.enabled({ usage }),
                value: experiment.value({ usage }),
                open: experiment.open({ usage }),
                readable: experiment.readableValue({ usage }),
                shortReadable: experiment.shortReadableValue({ usage })
            };
        },

        getTrainingUserCapabilities() {
            const state = get();
            const workspace = state.workspacesById?.[state.currentWorkspaceId];
            const usage = state.workspaceUsagesById?.[state.currentWorkspaceId];

            if (!usage || !workspace) return null;

            // Import plan utilities dynamically to avoid circular dependencies
            const {
                isSelfServePaidPlan,
                isFreePlan,
                isFreeOrTrialPlan
            } = require("@functions/workspacePlans");

            const plan = usage.plan || workspace.plan;
            const trainUsage = usage.train;
            const hasCreditSystem = !!usage.features?.creditSystem;
            const isPaidUbp = hasCreditSystem && !isFreeOrTrialPlan(usage);

            return {
                isPaidPlan: !isFreePlan(plan),
                canTrainAccurateModel: !!(trainUsage?.accurate || isSelfServePaidPlan(plan)),
                canTrainAllModels: !!(trainUsage?.canTrainAllModels || isSelfServePaidPlan(plan)),
                canTrainExtraLarge: !!trainUsage?.canTrainExtraLarge,
                canTrainNas: !!trainUsage?.canTrainNas,
                canTrainNewRFDETR: !!trainUsage?.canTrainNewRFDETR,
                canTrainSam3: !!(trainUsage?.canTrainSam3 || isPaidUbp)
            };
        },

        // mutators
        setIsSetup: (isSetup) => set(() => ({ isSetup }), false, "setIsSetup"),
        setSsoWorkspaceNoSeats: (noSeats, workspaceId) =>
            set(
                () => ({ ssoWorkspaceNoSeats: noSeats, ssoWorkspaceNoSeatsId: workspaceId }),
                false,
                "setSsoWorkspaceNoSeats"
            ),

        setCurrentWorkspaceId: (id: string) => {
            window.currentWorkspace = id;
            return set(() => ({ currentWorkspaceId: id }), false, "setWorkspaceId");
        },
        addWorkspaceById: (ws) => {
            window.workspacesById = Object.assign(window.workspacesById || {}, { [ws.id]: ws });
            window.workspacesByUrl = Object.assign(window.workspacesByUrl || {}, { [ws.url]: ws });
            return set(deepSet("workspacesById", { [ws.id]: ws }), false, "addWorkspaceById");
        },
        addWorkspaceUsageById: (usg) => {
            window.workspaceUsage = Object.assign({}, window.workspaceUsage || {}, {
                [usg.id]: usg
            });
            return set(
                deepSet("workspaceUsagesById", { [usg.id]: usg }),
                false,
                "setWorkspaceUsages"
            );
        },
        addWorkspaceDatasetsById: (workspaceId, datasets) => {
            const datasetMap = datasets?.reduce((acc, dataset) => {
                if (dataset?.id) {
                    acc[dataset.id] = dataset;
                }
                return acc;
            }, {});
            return set(
                deepSet("workspaceDatasetsById", {
                    [workspaceId]: { ...get().workspaceDatasetsById[workspaceId], ...datasetMap }
                }),
                false,
                "addWorkspaceDatasetsById"
            );
        },
        addWorkspaceStarredDatasetsById: (workspaceId, datasetIds) => {
            const newStateValue = Array.from(new Set([...datasetIds]));

            return set(
                deepSet("workspaceStarredDatasetsById", { [workspaceId]: newStateValue }),
                false,
                "addWorkspaceStarredDatasetsById"
            );
        },
        addWorkspacePlanHistoricalUsageDataById: (id, historicalUsageData) => {
            return set(
                deepSet("workspacePlanHistoricalUsageDataById", { [id]: historicalUsageData }),
                false,
                "workspacePlanHistoricalUsageDataById"
            );
        },
        setCurrentWorkspaceUBPCreditCapRestricted: (restricted) => {
            return set(
                () => ({ currentWorkspaceUBPCreditCapRestricted: restricted }),
                false,
                "setCurrentWorkspaceUBPCreditCapRestricted"
            );
        },
        fetchWorkspaceUBPStripeSubscriptions: async (id, refetch = false) => {
            if (get().workspaceUBPSubscriptionsById?.[id] && !refetch) return;
            set(
                deepSet("workspaceUBPSubscriptionsById", { [id]: { loading: true, error: null } }),
                false,
                "fetchWorkspaceStripeSubscriptions"
            );
            try {
                const subscriptions = await fetch(
                    `/query/get_workspace_ubp_stripe_subscriptions?workspaceId=${id}`
                ).then((res) => res.json());
                set(
                    deepSet("workspaceUBPSubscriptionsById", {
                        [id]: { loading: false, data: subscriptions }
                    }),
                    false,
                    "fetchWorkspaceStripeSubscriptions"
                );
            } catch (e) {
                set(
                    deepSet("workspaceUBPSubscriptionsById", {
                        [id]: { loading: false, error: e }
                    }),
                    false,
                    "fetchWorkspaceStripeSubscriptions"
                );
            }
        },
        fetchPaidPlanSubscription: async (id, { refetch } = {}) => {
            if (get().paidPlanSubscriptionById?.[id] && !refetch) return;
            set(
                deepSet("paidPlanSubscriptionById", { [id]: { loading: true, error: null } }),
                false,
                "fetchPaidPlanSubscription"
            );
            try {
                const currentSubscription = await fetch(
                    `/query/subscriptions?workspaceId=${id}`
                ).then((response) => response.json());
                set(
                    deepSet("paidPlanSubscriptionById", {
                        [id]: { loading: false, data: currentSubscription }
                    }),
                    false,
                    "fetchPaidPlanSubscription"
                );
            } catch (e) {
                set(
                    deepSet("paidPlanSubscriptionById", { [id]: { loading: false, error: e } }),
                    false,
                    "fetchPaidPlanSubscription"
                );
            }
        }
    };
}

export function buildWorkspaceStore(set, get, api) {
    return {
        restrictedByTOS: false,
        setRestrictedByTOS: (r: boolean) =>
            set((s) => ({ restrictedByTOS: r }), false, "setRestrictedByTOS"),

        ...workspaceReducer(set, get)
    };
}
